import React from 'react';

import { FormattedMessage, IntlShape } from 'react-intl';

import { CellContext, ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { createGenericSortableStatsCol, leftPaddedColumn, StatsColumnType } from '~/core/commonTableColumns';
import { Alignments, CountryCode } from '~/core/enums';
import { getPlayerHeight, parseValueFromLocalizedAttribute } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { DraftPick } from './types';

const SMALL_COLUMN_SIZE = 72;
const MEDIUM_COLUMN_SIZE = 144;

const columnHelper = createColumnHelper<DraftPick>();
const createDraftColumn = (intl: IntlShape, columnDef: StatsColumnType): ColumnDef<DraftPick> => {
    columnDef.alignHeader = columnDef.alignHeader || Alignments.left;
    columnDef.size = columnDef.size || 100;
    return {
        ...createGenericSortableStatsCol(columnHelper)(intl, columnDef),
        maxSize: columnDef.size,
    };
};

const columnDefinitions = (intl: IntlShape) => [
    createDraftColumn(intl, {
        acc: 'round',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.round,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.round, intl),
    }),
    createDraftColumn(intl, {
        acc: 'pickInRound',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.pick,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.pick, intl),
    }),
    createDraftColumn(intl, {
        acc: 'overallPick',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.overall,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.overall, intl),
    }),
    createDraftColumn(intl, {
        acc: 'teamAbbrev',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.team,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.team, intl),
    }),
    createDraftColumn(intl, {
        acc: 'teamPickHistory',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.teamHistory,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.teamHistory, intl),
    }),
    {
        ...createDraftColumn(intl, {
            acc: 'firstName',
            header: CommonMessages.playerInfoDescriptions.long.player,
            size: SMALL_COLUMN_SIZE,
            meta: {
                ...leftPaddedColumn(),
                ...addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.player, intl),
            },
        }),
        // This is the column which won't have a max size and will take up the remaining space in the table
        maxSize: undefined,
        cell: ({ row }) =>
            `${parseValueFromLocalizedAttribute(row.original.firstName)} ${parseValueFromLocalizedAttribute(
                row.original.lastName
            )}`,
        sortingFn: (a: Row<DraftPick>, b: Row<DraftPick>) => {
            const aName = `${parseValueFromLocalizedAttribute(a.original.lastName)} ${parseValueFromLocalizedAttribute(
                a.original.firstName
            )}`;
            const bName = `${parseValueFromLocalizedAttribute(b.original.lastName)} ${parseValueFromLocalizedAttribute(
                b.original.firstName
            )}`;
            return bName.toLowerCase().localeCompare(aName.toLowerCase());
        },
    },
    createDraftColumn(intl, {
        acc: 'positionCode',
        alignHeader: Alignments.center,
        header: CommonMessages.playerInfoDescriptions.abbreviations.position,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.position, intl),
    }),
    createDraftColumn(intl, {
        acc: 'countryCode',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.country,
        size: SMALL_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.country, intl),
    }),
    {
        ...createDraftColumn(intl, {
            acc: 'height',
            alignHeader: Alignments.center,
            header: CommonMessages.playerInfoDescriptions.long.height,
            size: SMALL_COLUMN_SIZE,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.height, intl),
        }),
        cell: ({ cell, row }: CellContext<DraftPick, number>) =>
            cell.getValue() ? (
                getPlayerHeight(CountryCode.UnitedStates, row.original.height)
            ) : (
                <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
            ),
        sortDescFirst: true,
    },
    {
        ...createDraftColumn(intl, {
            acc: 'weight',
            alignHeader: Alignments.center,
            header: CommonMessages.playerInfoDescriptions.long.weight,
            size: SMALL_COLUMN_SIZE,
            meta: addSortableHeaderTitle(CommonMessages.playerInfoDescriptions.long.weight, intl),
        }),
        sortDescFirst: true,
    },
    createDraftColumn(intl, {
        acc: 'amateurLeague',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.amateurLeague,
        size: MEDIUM_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.amateurLeague, intl),
    }),
    createDraftColumn(intl, {
        acc: 'amateurClubName',
        alignHeader: Alignments.center,
        header: CommonMessages.tableHeaders.long.amateurTeam,
        size: MEDIUM_COLUMN_SIZE,
        meta: addSortableHeaderTitle(CommonMessages.tableHeaders.long.amateurTeam, intl),
    }),
];

export default columnDefinitions;
